import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface ValidationError {
  line: number;
  column?: number;
  message: string;
  severity?: 'error' | 'warning' | 'info';
  source?: string;
}

interface ValidationPanelProps {
  errors: ValidationError[];
  onErrorClick?: (error: ValidationError) => void;
}

export function ValidationPanel({ errors = [], onErrorClick }: ValidationPanelProps) {
  const [expanded, setExpanded] = useState(true);

  const getSeverityIcon = (severity?: string) => {
    switch (severity) {
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getSeverityType = (e: ValidationError) => {
    const sev = (e.severity || 'error').toLowerCase();
    return sev === 'warning' ? 'warning' : 'error';
  };

  const errorCount = errors.filter((e) => getSeverityType(e) === 'error').length;
  const warningCount = errors.filter((e) => getSeverityType(e) === 'warning').length;

  const getBorderColor = () => {
    if (errorCount > 0) return 'border-red-500';
    if (warningCount > 0) return 'border-yellow-500';
    return 'border-blue-500';
  };

  return (
    <div className={cn('border-t-2 bg-card', getBorderColor())}>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        {/* Header */}
        <CollapsibleTrigger asChild>
          <div className="flex items-center px-4 py-1 cursor-pointer hover:bg-muted/50">
            <div className="flex items-center gap-4 flex-1">
              {errorCount > 0 && (
                <div className="flex items-center gap-1">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-semibold text-red-500">
                    {errorCount} error{errorCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {warningCount > 0 && (
                <div className="flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-semibold text-yellow-500">
                    {warningCount} warning{warningCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>
        </CollapsibleTrigger>

        {/* Error List */}
        <CollapsibleContent>
          <div className="max-h-[200px] overflow-auto">
            {errors.map((error, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-1 hover:bg-muted/50 cursor-pointer"
                onClick={() => onErrorClick && onErrorClick(error)}
              >
                {getSeverityIcon(getSeverityType(error))}
                <div className="flex items-center gap-2 flex-1">
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    Ln {error.line}
                    {error.column && `:${error.column}`}
                  </code>
                  <span
                    className={cn(
                      'text-sm',
                      getSeverityType(error) === 'warning' ? 'text-yellow-600' : 'text-red-600'
                    )}
                  >
                    {error.message}
                  </span>
                  {error.source === 'pyodide' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs">
                          Schema
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>Schema validation (Pydantic)</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default ValidationPanel;
